import { lruCache } from '~/services/cache-storage/lruCache';
import { convertLegacySeriesId, parseLegacyEventSeriesPath, pathLooksLikeLegacyEventSeries } from '~/utils/convertLegacyEventId';
import { legacyLocalesMapping } from '@modules/portal/config/router.config';

// `defaultLocale` is immutable runtime config; cache to avoid hitting Nitro's
// `getEnv`/`destr` on every legacy-route navigation.
let cachedDefaultLocale: string | undefined;
const getDefaultLocale = () => {
  if (cachedDefaultLocale === undefined) {
    cachedDefaultLocale = useRuntimeConfig().public.defaultLocale;
  }
  return cachedDefaultLocale;
};

export default defineNuxtRouteMiddleware(async (to) => {
  if (to.path.startsWith('/healthcheck')) {
    return;
  }
  // name is defined when nuxt knows the route exists
  if (to.name) {
    return;
  }

  const path = to.path;
  if (
    path.startsWith('/api/')
    || path.startsWith('/actuator')
    || path.startsWith('/i/')
    || path.startsWith('/__')
    || path.startsWith('/_nuxt')
    || path.startsWith('/_portal')
  ) {
    return;
  }
  if (!pathLooksLikeLegacyEventSeries(path)) {
    return;
  }

  const redirectOptions = { redirectCode: 301 };

  if (import.meta.client) {
    redirectOptions.external = true;
  }

  // Redirect series to search page with seriesId
  const pathObject = parseLegacyEventSeriesPath(path);
  if (!(pathObject && pathObject.id)) {
    return;
  }
  const language = pathObject.lang?.length === 2 ? pathObject.lang : legacyLocalesMapping[pathObject.lang as keyof typeof legacyLocalesMapping];
  const defaultLocale = getDefaultLocale();
  // TODO: Find proper way to detect legacy series ids
  if (pathObject.id.length === 5 || (pathObject.id.length === 6 && pathObject.id.startsWith('1'))) {
    // series ids are 5 characters long, temporary workaround is to redirect them to search page with seriesId
    const seriesHash = await convertLegacySeriesId(pathObject.id);
    const cookie = useCookie('legacyUrl');
    cookie.value = '1';
    return navigateTo(
      {
        path: `${language && language !== defaultLocale ? `/${language}` : ''}/series/${seriesHash}/${pathObject.slug || ''}`,
        query: to.query,
      }, redirectOptions);
  }

  const generatedId = await convertLegacyEventId(pathObject.id);
  if (!generatedId) {
    return;
  }

  // TODO: Change to redis cache?
  const cacheKey = `redirects:${generatedId}:${language || defaultLocale}`;
  let url = await lruCache.getItem(cacheKey) as string;
  if (!url) {
    if (pathObject.slug) {
      url = useEventPath({ id: generatedId, sluggedName: pathObject.slug || 'event' }, language);
    } else {
      const { eventPath } = await useEvent(generatedId, defaultLocale);

      if (!eventPath.value) {
        return;
      }

      url = eventPath.value;
    }

    if (!url) {
      return;
    }
    await lruCache.setItem(cacheKey, url);
  }

  delete to.query.concert; // Remove the concert query parameter if it exists
  delete to.query.design;
  delete to.query.lang;
  delete to.query.center;

  return navigateTo({ path: url, query: to.query }, redirectOptions);
});
